import React from "react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ComingSoonSkeleton from "./ComingSoonSkeleton";
import ErrorState from "../ErrorState/ErrorState";
import { useCachedFetch } from "../../hooks/useCachedFetch";
import { SUBSCRIBE_API } from "../../config/api";
import { getRecaptchaToken } from '../../utils/recaptcha';
import { useConsent } from '../../hooks/useConsent';
import { triggerShowBanner } from '../CookieBanner/CookieBanner';
import ErrorPage from '../../pages/404Page';
import { IMAGES_BASE_URL } from '../../config/constants';
import "./ComingSoon.scss";

const getTimeUnitLabel = (value, unit, language) => {
  const pluralRules = {
    ua: {
      days: ['день', 'дні', 'днів'],
      hours: ['година', 'години', 'годин'],
      minutes: ['хвилина', 'хвилини', 'хвилин'],
      seconds: ['секунда', 'секунди', 'секунд']
    },
    en: {
      days: ['day', 'days', 'days'],
      hours: ['hour', 'hours', 'hours'],
      minutes: ['minute', 'minutes', 'minutes'],
      seconds: ['second', 'seconds', 'seconds']
    },
    ru: {
      days: ['день', 'дня', 'дней'],
      hours: ['час', 'часа', 'часов'],
      minutes: ['минута', 'минуты', 'минут'],
      seconds: ['секунда', 'секунды', 'секунд']
    },
    pl: {
      days: ['dzień', 'dni', 'dni'],
      hours: ['godzina', 'godziny', 'godzin'],
      minutes: ['minuta', 'minuty', 'minut'],
      seconds: ['sekunda', 'sekundy', 'sekund']
    },
    de: {
      days: ['Tag', 'Tage', 'Tagen'],
      hours: ['Stunde', 'Stunden', 'Stunden'],
      minutes: ['Minute', 'Minuten', 'Minuten'],
      seconds: ['Sekunde', 'Sekunden', 'Sekunden']
    },
    es: {
      days: ['día', 'días', 'días'],
      hours: ['hora', 'horas', 'horas'],
      minutes: ['minuto', 'minutos', 'minutos'],
      seconds: ['segundo', 'segundos', 'segundos']
    }
  };

  const rules = pluralRules[language] || pluralRules.en;
  const forms = rules[unit] || rules.days;

  if (['en', 'de', 'es'].includes(language)) {
    return value === 1 ? forms[0] : forms[1];
  } else if (language === 'pl') {
    if (value === 1) return forms[0];
    const lastDigit = value % 10;
    const lastTwoDigits = value % 100;
    if ((lastDigit >= 2 && lastDigit <= 4) && !(lastTwoDigits >= 12 && lastTwoDigits <= 14)) {
      return forms[1];
    }
    return forms[2];
  } else {
    if (value % 10 === 1 && value % 100 !== 11) {
      return forms[0];
    } else if ([2, 3, 4].includes(value % 10) && ![12, 13, 14].includes(value % 100)) {
      return forms[1];
    } else {
      return forms[2];
    }
  }
};

const ComingSoonWrapper = ({ hideLanguageSwitcherSetter, hideHeaderFooterSetter }) => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;

  useEffect(() => {
    if (hideLanguageSwitcherSetter) hideLanguageSwitcherSetter(true);

    return () => {
      if (hideLanguageSwitcherSetter) hideLanguageSwitcherSetter(false);
    };
  }, [hideLanguageSwitcherSetter]);

  const { data: promoData, loading, error, refetch } = useCachedFetch('/data/locales/promo.json');

  const { hasConsentForForms } = useConsent();

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [show404, setShow404] = useState(false);

  const [timeLeft, setTimeLeft] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const comingSoonData = useMemo(() => {
    if (!promoData?.comingSoonData?.length) return null;

    const latest = promoData.comingSoonData.reduce(
      (prev, curr) => (curr.comingSoonId > prev.comingSoonId ? curr : prev),
      promoData.comingSoonData[0]
    );

    const translation = latest.translations?.[currentLang];

    const isActive = translation?.isActive === true;

    if (!isActive) return null;

    return {
      ...latest,
      startDateTimestamp: new Date(latest.startDate).getTime(),
      endDateTimestamp: new Date(latest.endDate).getTime(),
      ...(translation || {})
    };
  }, [promoData, currentLang]);

  useEffect(() => {
    if (loading) return;

    if (error) {
      setHasError(true);
      setShow404(false);
      setIsLoading(false);
      return;
    }

    if (comingSoonData) {
      setHasError(false);
      setShow404(false);
    } else {
      setShow404(true);
      setHasError(false);
    }

    setIsLoading(false);
  }, [loading, error, comingSoonData]);

  useEffect(() => {
    setEmailError("");
    setFormSubmitted(false);
    setIsFading(false);
    setFormKey(prev => prev + 1);
    setSubmitError('');
  }, [currentLang]);

  useEffect(() => {
    if (comingSoonData?.startDateTimestamp) {
      const timeUntilStart = comingSoonData.startDateTimestamp - Date.now();
      setTimeLeft(Math.max(timeUntilStart, 0));
      setIsInitialized(true);
    }
  }, [comingSoonData]);

  useEffect(() => {
    if (!isInitialized || timeLeft === null) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(prev - 1000, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [isInitialized, timeLeft]);

  useEffect(() => {
    if (!isInitialized || timeLeft === null) return;
    if (timeLeft <= 0 && comingSoonData?.slug) {
      navigate(`/${currentLang}/promo/${comingSoonData.slug}`);
    }
  }, [timeLeft, comingSoonData, navigate, currentLang, isInitialized]);

  const errorMessages = useMemo(() => ({
    en: {
      emailInvalid: "Invalid email address",
      emailDomain: "Email must contain a valid domain (e.g. gmail.com)",
      emailRequired: "Please enter your email address to subscribe to updates",
      keyboardLanguage: "Switch keyboard language",
      submitting: "Sending...",
      subscribeSuccess: "Thank you for subscribing!",
      subscribeError: "Subscription error. Please try again.",
      emailAlreadySubscribed: "This email is already subscribed",
      rateLimitError: "Too many requests. Please try again later.",
      consentError: "Consent to personal data processing is required to subscribe",
    },
    ua: {
      emailInvalid: "Невірна email адреса",
      emailDomain: "Email повинен містити дійсний домен (наприклад, gmail.com)",
      emailRequired: "Якщо бажаєте продовжити підписку на оновлення введіть вашу електронну адресу",
      keyboardLanguage: "Змініть мову на клавіатурі",
      submitting: "Відправка...",
      subscribeSuccess: "Дякуємо за підписку!",
      subscribeError: "Помилка підписки. Спробуйте ще раз.",
      emailAlreadySubscribed: "Цей email вже підписаний",
      rateLimitError: "Занадто багато запитів. Спробуйте пізніше.",
      consentError: "Для підписки необхідна згода на обробку персональних даних",
    },
    ru: {
      emailInvalid: "Неверный адрес электронной почты",
      emailDomain: "Email должен содержать действительный домен (например, gmail.com)",
      emailRequired: "Если хотите подписаться на обновления, введите ваш адрес электронной почты",
      keyboardLanguage: "Смените язык на клавиатуре",
      submitting: "Отправка...",
      subscribeSuccess: "Спасибо за подписку!",
      subscribeError: "Ошибка подписки. Попробуйте еще раз.",
      emailAlreadySubscribed: "Этот email уже подписан",
      rateLimitError: "Слишком много запросов. Попробуйте позже.",
      consentError: "Для подписки необходимо согласие на обработку персональных данных",
    },
    pl: {
      emailInvalid: "Nieprawidłowy adres email",
      emailDomain: "Email musi zawierać prawidłową domenę (np. gmail.com)",
      emailRequired: "Proszę wprowadzić swój adres email, aby subskrybować aktualizacje",
      keyboardLanguage: "Zmień język klawiatury",
      submitting: "Wysyłanie...",
      subscribeSuccess: "Dziękujemy za subskrypcję!",
      subscribeError: "Błąd subskrypcji. Spróbuj ponownie.",
      emailAlreadySubscribed: "Ten email jest już zapisany",
      rateLimitError: "Zbyt wiele żądań. Spróbuj później.",
      consentError: "Do subskrypcji wymagana jest zgoda na przetwarzanie danych osobowych",
    },
    de: {
      emailInvalid: "Ungültige E-Mail-Adresse",
      emailDomain: "E-Mail muss eine gültige Domain enthalten (z.B. gmail.com)",
      emailRequired: "Bitte geben Sie Ihre E-Mail-Adresse ein, um Updates zu abonnieren",
      keyboardLanguage: "Tastatursprache wechseln",
      submitting: "Senden...",
      subscribeSuccess: "Vielen Dank für Ihr Abonnement!",
      subscribeError: "Abonnementfehler. Bitte versuchen Sie es erneut.",
      emailAlreadySubscribed: "Diese E-Mail ist bereits abonniert",
      rateLimitError: "Zu viele Anfragen. Bitte versuchen Sie es später erneut.",
      consentError: "Für das Abonnement ist die Zustimmung zur Verarbeitung personenbezogener Daten erforderlich",
    },
    es: {
      emailInvalid: "Dirección de correo electrónico no válida",
      emailDomain: "El correo electrónico debe contener un dominio válido (por ejemplo, gmail.com)",
      emailRequired: "Por favor ingrese su dirección de correo electrónico para suscribirse a las actualizaciones",
      keyboardLanguage: "Cambiar idioma del teclado",
      submitting: "Enviando...",
      subscribeSuccess: "¡Gracias por suscribirte!",
      subscribeError: "Error de suscripción. Inténtalo de nuevo.",
      emailAlreadySubscribed: "Este correo electrónico ya está suscrito",
      rateLimitError: "Demasiadas solicitudes. Por favor, inténtelo más tarde.",
      consentError: "Para suscribirse se requiere el consentimiento para el procesamiento de datos personales",
    },
  }), []);

  const currentErrors = useMemo(() =>
    errorMessages[currentLang] || errorMessages.en,
    [currentLang, errorMessages]
  );

  const SignupSchema = useMemo(() =>
    Yup.object({
      email: Yup.string()
        .email(currentErrors.emailInvalid)
        .matches(
          /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
          currentErrors.emailDomain
        )
        .required(currentErrors.emailRequired),
    }),
    [currentErrors]
  );

  const handleSubmit = useCallback(async (values, { resetForm }) => {
    if (!hasConsentForForms()) {
      setSubmitError(currentErrors.consentError);
      setFormSubmitted(false);
      setIsSubmitting(false);
      triggerShowBanner();
      setTimeout(() => {
        setIsFading(true);
        setTimeout(() => {
          setFormSubmitted(false);
          setIsFading(false);
          setSubmitError('');
        }, 1000);
      }, 10000);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      let recaptchaToken = '';
      try {
        recaptchaToken = await getRecaptchaToken('subscribe');
      } catch (err) {
        console.warn('reCAPTCHA error:', err);
      }

      const response = await fetch(SUBSCRIBE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          language: currentLang,
          recaptchaToken: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'form_lead_submitted',
          form_name: 'newsletter_subscription'
        });

        setFormSubmitted(true);
        setSubmitError('');
        resetForm();

        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setFormSubmitted(false);
            setIsFading(false);
            setIsSubmitting(false);
          }, 1000);
        }, 3000);
      } else if (response.status === 429) {
        setSubmitError(currentErrors.rateLimitError);
        setFormSubmitted(true);
        setIsSubmitting(false);
        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setFormSubmitted(false);
            setIsFading(false);
            setSubmitError('');
          }, 1000);
        }, 3000);
      } else {
        let userMessage = '';

        if (data.message && (
          data.message.includes('already subscribed') ||
          data.message.includes('вже підписаний') ||
          data.message.includes('уже подписан')
        )) {
          userMessage = currentErrors.emailAlreadySubscribed;
        } else {
          userMessage = data.errors?.[0]?.msg || data.message || currentErrors.subscribeError;
        }

        setSubmitError(userMessage);
        setFormSubmitted(true);
        setIsSubmitting(false);

        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setFormSubmitted(false);
            setIsFading(false);
            setSubmitError('');
          }, 1000);
        }, 3000);
      }
    } catch (error) {
      console.error('Network error:', error);
      setFormSubmitted(true);
      setSubmitError(currentErrors.subscribeError);
      setIsSubmitting(false);

      setTimeout(() => {
        setIsFading(true);
        setTimeout(() => {
          setFormSubmitted(false);
          setIsFading(false);
          setSubmitError('');
        }, 1000);
      }, 3000);
    }
  }, [currentLang, currentErrors, hasConsentForForms]);

  const handleEmailInput = useCallback((e, setFieldTouched, setFieldError) => {
    const value = e.target.value;
    if (value && /[А-Яа-яЁё]/.test(value)) {
      setEmailError(currentErrors.keyboardLanguage);
    } else {
      setEmailError("");
    }
    e.target.value = value.replace(/[^A-Za-z0-9@._-]/g, "");
    if (value) {
      setFieldTouched("email", true, false);
      setFieldError("email", "");
      setSubmitError('');
    }
  }, [currentErrors]);

  const timeUnits = useMemo(() => {
    if (timeLeft === null) return null;

    const totalSeconds = Math.floor(timeLeft / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      days: {
        value: days,
        label: getTimeUnitLabel(days, 'days', currentLang)
      },
      hours: {
        value: hours,
        label: getTimeUnitLabel(hours, 'hours', currentLang)
      },
      minutes: {
        value: minutes,
        label: getTimeUnitLabel(minutes, 'minutes', currentLang)
      },
      seconds: {
        value: seconds,
        label: getTimeUnitLabel(seconds, 'seconds', currentLang)
      },
    };
  }, [timeLeft, currentLang]);

  const formatNumber = useCallback((num) => String(num).padStart(2, "0"), []);

  if (show404) {
    return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} />;
  }

  if (isLoading) {
    return <ComingSoonSkeleton />;
  }

  if (hasError || !comingSoonData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return (
    <div className="comingsoon__wrapper">
      <div
        className="comingsoon__item"
        style={{
          backgroundImage: comingSoonData.saleImage
            ? `url(${IMAGES_BASE_URL}${comingSoonData.saleImage})`
            : "none",
        }}
      >
        <div className="comingsoon__gradient"></div>

        <div className="comingsoon__info">
          <p className="comingsoon__info__accent">{comingSoonData.campaignSubtitle}</p>
          <p className="comingsoon__info__subtitle">{comingSoonData.campaignName}</p>
          <p className="comingsoon__info__text">
            {comingSoonData.campaignPeriod
              ?.replace("{startDate}", comingSoonData.startDate || "")
              ?.replace("{endDate}", comingSoonData.endDate || "")}
          </p>
          <h2 className="comingsoon__info__title">
            {comingSoonData.comingSoonInfo?.comingSoonTitle}
          </h2>
          <p className="comingsoon__info__accent">
            {comingSoonData.comingSoonInfo?.infoSubtitle}
          </p>
        </div>

        {timeUnits && (
          <div className="comingsoon__timer">
            <div className="comingsoon__timer__numbers">
              <div className="numbers__item">{formatNumber(timeUnits.days.value)}</div>
              <div className="numbers__item">{formatNumber(timeUnits.hours.value)}</div>
              <div className="numbers__item">{formatNumber(timeUnits.minutes.value)}</div>
              <div className="numbers__item">{formatNumber(timeUnits.seconds.value)}</div>
            </div>
            <div className="comingsoon__timer__labels">
              <div className="labels__item">{timeUnits.days.label}</div>
              <div className="labels__item">{timeUnits.hours.label}</div>
              <div className="labels__item">{timeUnits.minutes.label}</div>
              <div className="labels__item">{timeUnits.seconds.label}</div>
            </div>
          </div>
        )}
        <div className="subscribe__comingsoon">
          <p className="subscribe__comingsoon__text">
            {comingSoonData.comingSoonInfo?.subscribeText}
          </p>
          <Formik
            key={formKey}
            initialValues={{ email: "" }}
            validationSchema={SignupSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleSubmit, setFieldTouched, setFieldError }) => (
              <Form className="subscribe__form" onSubmit={handleSubmit}>
                <div className="subscribe__form__input__wrapper">
                  <Field
                    name="email"
                    type="email"
                    id="email"
                    placeholder=" "
                    autoComplete="email"
                    onInput={(e) => handleEmailInput(e, setFieldTouched, setFieldError)}
                  />
                  <label htmlFor="email">
                    {comingSoonData.comingSoonInfo?.subscribeInputText}
                  </label>
                  {emailError && !values.email && (
                    <div className="switch__keyboard__error">{emailError}</div>
                  )}
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="error__message"
                  />
                  {submitError && submitError === currentErrors.consentError && (
                    <div className="error__message">
                      <p>{submitError}</p>
                    </div>
                  )}
                </div>
                <div className="subscribe__form__button__wrapper">
                  {formSubmitted ? (
                    <div
                      className={`form__success__message ${isFading ? "fade-out" : "fade-in"}`}
                    >
                      <p>
                        {submitError
                          ? submitError
                          : (comingSoonData.comingSoonInfo?.subscribeSuccess ||
                            t('thankYouForSubscription', 'Дякуємо за підписку!'))
                        }
                      </p>
                    </div>
                  ) : (
                    <button
                      type="submit"
                      className="subscribe__button"
                      disabled={isSubmitting}
                    >
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      {isSubmitting
                        ? currentErrors.submitting
                        : comingSoonData.comingSoonInfo?.subscribeButtonText
                      }
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ComingSoonWrapper);